import {
  Ref,
  onMounted,
  onBeforeUnmount,
  onUpdated,
  onBeforeUpdate,
  ref,
  provide,
  inject,
  watch,
  onBeforeMount,
  ComponentPublicInstance
} from "vue";

interface SyncGroup<T> {
  refItems: Set<RefItem>;
  resolvers: Set<UpdateResolver<T>>;
}

type LayoutSyncTargets = HTMLElement | ComponentPublicInstance | null;
type LayoutReader<T> = (refItems: LayoutSyncTargets) => T;
type UpdateResolver<T> = (results: T) => void;
type RefItem = Ref<LayoutSyncTargets>;

export type Dimensions = {
  height: number;
  width: number;
  top: number;
  left: number;
};

// this class is used to batch layout reads to the DOM from multiple components to prevent
// layout thrashing
class LayoutSync {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groups: Record<string, SyncGroup<any>>;
  watchers: Record<string, Function>;
  shouldProcessTasks: boolean;

  constructor() {
    this.groups = {};
    // watchers are callbacks that run on every flush until explicitly removed.
    this.watchers = {};
    // shouldProcessTasks is true only when there is new work to be done
    this.shouldProcessTasks = true;
  }

  // flush jobs
  async flushJobs() {
    requestAnimationFrame(() => {
      if (!this.shouldProcessTasks) return;
      for (const uid of Object.keys(this.watchers)) {
        const cb = this.watchers[uid];
        cb();
      }
      this.shouldProcessTasks = false;
    });
  }

  createWatcher<T>(
    groupName: string,
    refItem: RefItem,
    computer: LayoutReader<T>,
    resolver: (result: T) => void
  ) {
    const group = (this.groups[groupName] = this.groups[groupName] || {
      refItems: new Set(),
      resolvers: new Set()
    });
    group.refItems.add(refItem);
    group.resolvers.add(resolver);
    this.watchers[groupName] = function() {
      const refArray = Array.from(group.refItems);
      const refItem = refArray[0].value;
      const result = computer(refItem);
      group.resolvers.forEach(resolver => resolver(result));
    };
  }

  removeWatcher<T>(
    groupName: string,
    el: Ref<LayoutSyncTargets>,
    resolver: (result: T) => void
  ) {
    const group = this.groups[groupName];
    group.refItems.delete(el);
    if (!group.refItems.size) {
      delete this.watchers[groupName];
    }
    group.resolvers.delete(resolver);
    if (!group.refItems.size) {
      delete this.watchers[groupName];
    }
  }

  recalculate() {
    this.shouldProcessTasks = true;
  }

  /**
   * This function should be called when the layout has been changed via an
   * effect outside of vue lifecycle.
   */
  layoutEffect() {
    this.shouldProcessTasks = true;
    this.flushJobs();
  }
}

const layoutSync = new LayoutSync();

window.addEventListener("resize", () => {
  layoutSync.layoutEffect();
});

let uid = 0;

function generateUID() {
  return `${uid++}`;
}

export function createLayoutContext(
  { win }: { win: Window } = { win: window }
) {
  const layoutSync = new LayoutSync();
  function resizeListener() {
    layoutSync.layoutEffect();
  }

  onMounted(() => {
    win.addEventListener("resize", resizeListener);
  });

  onBeforeUnmount(() => {
    win.removeEventListener("resize", resizeListener);
  });

  return layoutSync;
}

const layoutSyncSymbol = Symbol("layout-sync");

export function provideLayoutSync(context: LayoutSync) {
  provide(layoutSyncSymbol, context);
}

export function useLayoutContext() {
  const result = inject<LayoutSync>(layoutSyncSymbol);

  if (result) return result;
  return layoutSync;
}

export function useDimensionSync(
  el: RefItem,
  { groupName }: { groupName?: string } = {}
) {
  const dimensions = ref({ top: 0, left: 0, height: 0, width: 0 });
  const uid = generateUID();
  const watcherKey = groupName || uid;
  const layoutContext = useLayoutContext();

  function layoutReader(refItem: LayoutSyncTargets) {
    let el: HTMLElement;

    if (!refItem) {
      return {
        height: 0,
        width: 0,
        left: 0,
        top: 0
      };
    }

    if ("$el" in refItem) {
      el = refItem.$el as HTMLElement;
    } else {
      el = refItem;
    }

    return {
      height: el.offsetHeight,
      width: el.offsetWidth,
      top: el.offsetTop,
      left: el.offsetLeft
    };
  }

  function resolver(result: ReturnType<typeof layoutReader>) {
    dimensions.value.width = result.width;
    dimensions.value.height = result.height;
    dimensions.value.top = result.top;
    dimensions.value.left = result.left;
  }

  layoutContext.createWatcher(watcherKey, el, layoutReader, resolver);

  onBeforeMount(() => {
    layoutContext.recalculate();
  });

  onMounted(() => {
    layoutContext.flushJobs();
  });

  onBeforeUnmount(() => {
    layoutContext.removeWatcher(watcherKey, el, resolver);
  });

  onBeforeUpdate(() => {
    layoutContext.recalculate();
  });

  onUpdated(() => {
    layoutContext.flushJobs();
  });

  return { dimensions };
}

/**
 * Use syncLayoutDependencies to trigger layout recalculations on changes
 * to its dependencies. This is neccessary when a dependency is used to update
 * the dom layout.
 * @example
 * // When using prop to effect the width of an element
 * setup(props) {
 *   // convert prop to a ref
 *   const widthRef = computed(() => props.width)
 *   // changes to the width prop will now cause a layout sync recalculate
 *   syncLayoutDependencies([widthRef])
 *   return () => <div style={{ width: `${widthRef.value}px` }}></div>
 * }
 * @param dependencies
 * @param layoutContextOverride
 */
export function syncLayoutDependencies(
  dependencies: Ref<unknown>[],
  layoutContextOverride?: LayoutSync
) {
  const layoutContext = layoutContextOverride || useLayoutContext();
  const shouldRecalc = ref(false);
  watch(
    () => dependencies.map(dep => dep.value),
    () => {
      shouldRecalc.value = true;
    },
    { deep: true }
  );

  onBeforeUpdate(() => {
    if (shouldRecalc.value) {
      layoutContext.recalculate();
    }
    shouldRecalc.value = false;
  });

  onUpdated(() => {
    layoutContext.flushJobs();
  });

  onBeforeMount(() => {
    layoutContext.recalculate();
  });

  onMounted(() => {
    layoutContext.flushJobs();
  });
}

/**
 * Provides a layoutEffect method for the current layout context
 */
export function useLayoutEffect() {
  const layoutContext = useLayoutContext();
  /**
   * This function should be called when the layout has been changed via an
   * effect outside of vue lifecycle.
   */
  return () => layoutContext.layoutEffect();
}
